import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AudioStreamer } from "../lib/audio-streamer";
import { audioContext } from "../lib/utils";
import VolMeterWorket from "../lib/worklets/vol-meter";
import { ServerVoiceClient } from "../lib/server-voice-client";
import { ServerConnectOptions } from "../types";

export type UseLiveAPIResults = {
  client: ServerVoiceClient;
  connected: boolean;
  connecting: boolean;
  recovering: boolean;
  connect: (options: ServerConnectOptions) => Promise<void>;
  disconnect: () => Promise<void>;
  waitForPlaybackIdle: (timeoutMs?: number) => Promise<void>;
  setPlaybackMuted: (muted: boolean) => void;
  volume: number;
  analyser: AnalyserNode | null;
  lastCloseCode: number | null;
  lastCloseReason: string | null;
};

export function useLiveAPI(
  audioInitialBufferMs: number = 500,
  outputPrimeOnConnect: boolean = true
): UseLiveAPIResults {
  const client = useMemo(() => new ServerVoiceClient(), []);
  const audioStreamerRef = useRef<AudioStreamer | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const analyserConnectedGainRef = useRef<GainNode | null>(null);
  const desiredConnectionRef = useRef(false);
  const lastConnectOptionsRef = useRef<ServerConnectOptions | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);

  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [volume, setVolume] = useState(0);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [lastCloseCode, setLastCloseCode] = useState<number | null>(null);
  const [lastCloseReason, setLastCloseReason] = useState<string | null>(null);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current !== null) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    const options = lastConnectOptionsRef.current;
    if (!desiredConnectionRef.current || !options || reconnectTimeoutRef.current !== null) {
      return;
    }

    const attempt = reconnectAttemptRef.current + 1;
    reconnectAttemptRef.current = attempt;
    const delayMs = Math.min(1000 * 2 ** (attempt - 1), 5000);
    setRecovering(true);

    reconnectTimeoutRef.current = window.setTimeout(() => {
      reconnectTimeoutRef.current = null;
      if (!desiredConnectionRef.current || client.status !== "disconnected") {
        return;
      }

      setConnecting(true);
      client.connect(options).catch((error) => {
        console.error("[useLiveAPI] Reconnect failed:", error);
        setConnecting(false);
        scheduleReconnect();
      });
    }, delayMs);
  }, [client]);

  const ensureAnalyserConnected = useCallback(() => {
    const streamer = audioStreamerRef.current;
    const analyserNode = analyserRef.current;
    if (!streamer || !analyserNode) {
      return;
    }

    if (analyserConnectedGainRef.current === streamer.gainNode) {
      return;
    }

    try {
      streamer.gainNode.connect(analyserNode);
      analyserConnectedGainRef.current = streamer.gainNode;
    } catch (error) {
      console.error("[useLiveAPI] Failed to connect analyser:", error);
    }
  }, []);

  useEffect(() => {
    if (!audioStreamerRef.current) {
      audioContext({ id: "audio-out" }).then((audioCtx: AudioContext) => {
        audioStreamerRef.current = new AudioStreamer(audioCtx, {
          initialBufferMs: audioInitialBufferMs,
        });

        const newAnalyser = audioCtx.createAnalyser();
        newAnalyser.fftSize = 256;
        newAnalyser.smoothingTimeConstant = 0.8;
        analyserRef.current = newAnalyser;
        setAnalyser(newAnalyser);
        ensureAnalyserConnected();

        audioStreamerRef.current
          .addWorklet<any>("vumeter-out", VolMeterWorket, (ev: any) => {
            setVolume(ev.data.volume);
          })
          .catch((error) => {
            console.error("[useLiveAPI] Failed to register output worklet:", error);
          });
      });
    }
  }, [audioInitialBufferMs, ensureAnalyserConnected]);

  useEffect(() => {
    audioStreamerRef.current?.setInitialBufferMs(audioInitialBufferMs);
  }, [audioInitialBufferMs]);

  useEffect(() => {
    const onOpen = () => {
      clearReconnectTimeout();
      reconnectAttemptRef.current = 0;
      if (lastConnectOptionsRef.current?.forceNew) {
        lastConnectOptionsRef.current = {
          ...lastConnectOptionsRef.current,
          forceNew: false,
        };
      }
      setRecovering(false);
      ensureAnalyserConnected();
      setConnected(true);
      setConnecting(false);
      setLastCloseCode(null);
      setLastCloseReason(null);

      void (async () => {
        try {
          await audioStreamerRef.current?.resume();
          if (outputPrimeOnConnect) {
            await audioStreamerRef.current?.primeOutput();
          }
        } catch (error) {
          console.error("[useLiveAPI] Failed to prime audio output:", error);
        } finally {
          client.sendPlaybackReady();
        }
      })();
    };

    const onClose = (event?: CloseEvent) => {
      setConnected(false);
      setConnecting(false);
      setLastCloseCode(event?.code ?? null);
      setLastCloseReason(event?.reason ?? null);
      audioStreamerRef.current?.stop();
      if (desiredConnectionRef.current && event?.code !== 1000) {
        scheduleReconnect();
      } else {
        setRecovering(false);
      }
    };

    const onError = (event: Event) => {
      console.error("[useLiveAPI] socket error:", event);
      setConnecting(false);
    };

    const stopAudioStreamer = () => audioStreamerRef.current?.stop();

    const onAudio = (data: ArrayBuffer) => {
      ensureAnalyserConnected();
      audioStreamerRef.current?.addPCM16(new Uint8Array(data));
    };

    client
      .on("open", onOpen)
      .on("close", onClose)
      .on("error", onError)
      .on("interrupted", stopAudioStreamer)
      .on("audio", onAudio);

    return () => {
      client
        .off("open", onOpen)
        .off("close", onClose)
        .off("error", onError)
        .off("interrupted", stopAudioStreamer)
        .off("audio", onAudio);
      clearReconnectTimeout();
      desiredConnectionRef.current = false;
      setRecovering(false);
      client.disconnect();
    };
  }, [clearReconnectTimeout, client, ensureAnalyserConnected, outputPrimeOnConnect, scheduleReconnect]);

  const connect = useCallback(
    async (options: ServerConnectOptions) => {
      desiredConnectionRef.current = true;
      lastConnectOptionsRef.current = options;
      clearReconnectTimeout();
      setRecovering(false);
      setConnecting(true);
      setLastCloseCode(null);
      setLastCloseReason(null);
      try {
        await client.connect(options);
      } catch (error) {
        scheduleReconnect();
        throw error;
      }
    },
    [clearReconnectTimeout, client, scheduleReconnect]
  );

  const disconnect = useCallback(async () => {
    desiredConnectionRef.current = false;
    reconnectAttemptRef.current = 0;
    clearReconnectTimeout();
    setRecovering(false);
    client.disconnect();
    setConnected(false);
    setConnecting(false);
  }, [clearReconnectTimeout, client]);

  const setPlaybackMuted = useCallback((muted: boolean) => {
    const streamer = audioStreamerRef.current;
    if (!streamer) return;
    const ctx = streamer.gainNode.context;
    if (muted) {
      streamer.gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.08);
    } else {
      streamer.gainNode.gain.setValueAtTime(1, ctx.currentTime);
    }
  }, []);

  const waitForPlaybackIdle = useCallback(async (timeoutMs: number = 15000) => {
    await audioStreamerRef.current?.waitForPlaybackIdle(timeoutMs);
  }, []);

  return {
    client,
    connected,
    connecting,
    recovering,
    connect,
    disconnect,
    waitForPlaybackIdle,
    setPlaybackMuted,
    volume,
    analyser,
    lastCloseCode,
    lastCloseReason,
  };
}
